import React, { useMemo, useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Zoom, Pagination } from 'swiper/modules';
import { FiX, FiHeart, FiDownload, FiShare2, FiTrash2, FiRotateCw } from 'react-icons/fi';
import { FaHeart } from 'react-icons/fa';
import PhotoViewerImage from './PhotoViewerImage';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/zoom';
import 'swiper/css/pagination';
import '../styles/MobilePhotoSwiper.css';

export default function MobilePhotoSwiper({ 
  images, 
  initialIndex = 0, 
  onClose, 
  onLike,
  onRotate,
  onDelete,
  onShare,
  allowDownload = true,
  showActions = true,
  thumbnailVariant = 'sm'
}) {
  // Ensure images is always an array
  const safeImages = Array.isArray(images) ? images : [];
  if (safeImages.length === 0) {
    onClose?.();
    return null;
  }
  
  const [currentImage, setCurrentImage] = useState(safeImages[initialIndex] || safeImages[0]);
  const [activeIndex, setActiveIndex] = useState(Math.min(initialIndex, safeImages.length - 1));
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);

  useEffect(() => {
    if (safeImages[activeIndex]) {
      setCurrentImage(safeImages[activeIndex]);
    }
  }, [activeIndex, safeImages]);

  // Prevent body scroll when viewer is open
  useEffect(() => {
    const body = document.body;
    const hasAppShell = body.classList.contains('app-shell-active');
    const prevStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      width: body.style.width,
      top: body.style.top,
    };
    const scrollY = window.scrollY;

    body.style.overflow = 'hidden';
    body.style.width = '100%';
    if (!hasAppShell) {
      // Preserve scroll position when the body is the scroll container.
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
    }
    return () => {
      body.style.overflow = prevStyles.overflow;
      body.style.position = prevStyles.position;
      body.style.width = prevStyles.width;
      body.style.top = prevStyles.top;
      if (!hasAppShell) {
        window.scrollTo(0, scrollY);
      }
    };
  }, []);

  const authToken = useMemo(() => {
    try {
      return window?.localStorage?.getItem('auth_token');
    } catch {
      return null;
    }
  }, []);
  const tokenParam = ''; // Same-origin authenticated session cookie.

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `/media/originals/${currentImage.filename}${tokenParam}`;
    link.download = currentImage.title || currentImage.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle swipe down to close
  const handleTouchStart = (e) => {
    setStartY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY;
    
    // Only allow downward swipe
    if (deltaY > 0) {
      setTranslateY(deltaY);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    
    // If swiped down more than 100px, close the viewer
    if (translateY > 100) {
      onClose?.();
    } else {
      // Animate back to original position
      setTranslateY(0);
    }
  };

  return (
    <div 
      className="mobile-photo-swiper"
      style={{
        transform: `translateY(${translateY}px)`,
        transition: isDragging ? 'none' : 'transform 0.3s ease-out',
        opacity: translateY > 0 ? 1 - (translateY / 300) : 1
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header */}
      <div className="swiper-header">
        <div className="swiper-counter">
          {activeIndex + 1} / {safeImages.length}
        </div>
        <button className="swiper-close" onClick={onClose}>
          <FiX size={32} />
        </button>
      </div>

      {/* Swiper Gallery */}
      <Swiper
        modules={[Zoom, Pagination]}
        initialSlide={initialIndex}
        spaceBetween={20}
        slidesPerView={1}
        zoom={{
          maxRatio: 3,
          minRatio: 1,
          toggle: true
        }}
        pagination={{
          clickable: false,
          dynamicBullets: true,
          dynamicMainBullets: 5
        }}
        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        className="photo-swiper"
        touchEventsTarget="container"
        threshold={5}
        touchRatio={1}
        touchAngle={45}
        simulateTouch={true}
        allowTouchMove={true}
        grabCursor={false}
        preventInteractionOnTransition={true}
        touchStartPreventDefault={true}
        touchMoveStopPropagation={false}
        resistance={true}
        resistanceRatio={0.85}
      >
        {safeImages.map((image, index) => (
          <SwiperSlide key={image.id || index}>
            <div className="swiper-zoom-container">
              <PhotoViewerImage
                image={image}
                active={Math.abs(index - activeIndex) <= 1}
                thumbnailVariant={thumbnailVariant}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Actions Bar */}
      {showActions && (
        <div className="swiper-actions">
          <div className="action-buttons">
            {onLike && (
              <button 
                className="action-btn"
                onClick={() => onLike(currentImage.id, currentImage.is_favorite)}
              >
                {currentImage.is_favorite ? (
                  <FaHeart size={22} color="#ef4444" />
                ) : (
                  <FiHeart size={22} />
                )}
              </button>
            )}
            
            {onShare && (
              <button className="action-btn" onClick={() => onShare(currentImage)}>
                <FiShare2 size={22} />
              </button>
            )}

            {onRotate && (
              <button className="action-btn" onClick={() => onRotate(currentImage.id)}>
                <FiRotateCw size={22} />
              </button>
            )}
            
            {allowDownload && (
              <button className="action-btn" onClick={handleDownload}>
                <FiDownload size={22} />
              </button>
            )}
            
            {onDelete && (
              <button className="action-btn danger" onClick={() => onDelete(currentImage.id)}>
                <FiTrash2 size={22} />
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
